/**
 * Géométrie du calque d'interaction des studios (ADR-146 addendum (13)) : boîtes en coordonnées de la
 * fenêtre (le calque est en `position: fixed`, au-dessus de l'aperçu réduit), côté d'un dépôt, ligne
 * d'insertion. Pures, sans état ; le DOM n'est que lu.
 */

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

export type Axis = 'x' | 'y';
export type Side = 'before' | 'after';

export function boxOf(element: Element): Box {
  const rect = element.getBoundingClientRect();
  return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
}

export const sameBox = (a: Box | null | undefined, b: Box | null | undefined): boolean =>
  a === b || (a !== null && a !== undefined && b !== null && b !== undefined && Math.abs(a.left - b.left) < 0.5 && Math.abs(a.top - b.top) < 0.5 && Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5);

/** Axe d'une suite d'éléments : en ligne si leur conteneur est une flexbox en rangée, sinon en colonne. */
export function axisOf(element: Element): Axis {
  const parent = element.parentElement;
  if (parent === null || typeof getComputedStyle !== 'function') return 'y';
  const style = getComputedStyle(parent);
  return style.display.includes('flex') && !style.flexDirection.startsWith('column') ? 'x' : 'y';
}

/** Avant ou après une boîte, selon la moitié où se trouve le pointeur. */
export function sideOf(box: Box, x: number, y: number, axis: Axis): Side {
  return axis === 'x' ? (x < box.left + box.width / 2 ? 'before' : 'after') : y < box.top + box.height / 2 ? 'before' : 'after';
}

/** Ligne d'insertion de 3 px au bord d'une boîte. */
export function lineAt(box: Box, side: Side, axis: Axis): Box {
  const thickness = 3;
  if (axis === 'x') return { left: (side === 'before' ? box.left : box.left + box.width) - thickness / 2 - 2, top: box.top, width: thickness, height: box.height };
  return { left: box.left, top: (side === 'before' ? box.top : box.top + box.height) - thickness / 2 - 2, width: box.width, height: thickness };
}

/** Ligne au bas de l'intérieur d'une boîte (dépôt « à la fin » d'une région). */
export function lineInside(box: Box): Box {
  return { left: box.left + 8, top: box.top + box.height - 8, width: Math.max(0, box.width - 16), height: 3 };
}
